import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { addressesService } from "@/services/addressesService";
import type { AddressCreateDto, AddressUpdateDto } from "@/types/address.dto";
import { queryKeys } from "./keys";

export function useAddresses(enabled = true) {
  return useQuery({
    queryKey: queryKeys.addresses.all,
    queryFn: () => addressesService.getAll(),
    enabled,
  });
}

export function useAddressById(id: number, enabled = true) {
  return useQuery({
    queryKey: queryKeys.addresses.detail(id),
    queryFn: () => addressesService.getById(id),
    enabled: enabled && id > 0,
  });
}

export function useCreateAddressMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: AddressCreateDto) => addressesService.create(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.addresses.all });
    },
  });
}

export function useUpdateAddressMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }: { id: number; dto: AddressUpdateDto }) =>
      addressesService.update(id, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.addresses.all });
    },
  });
}

export function useDeleteAddressMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => addressesService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.addresses.all });
    },
  });
}

export function useSetDefaultAddressMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => addressesService.setDefault(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.addresses.all });
    },
  });
}
